import axios from "axios";

// Base URL for the analytics chat backend, following the same
// REACT_APP_* + createAxiosInstance pattern as src/commonApi.js.
const ANALYTICS_CHAT_URL =
  process.env.REACT_APP_ANALYTICS_CHAT_URL ||
  "https://localhost:9202/device_mgmt_service/v1/analytics/chat";

// Same factory shape as commonApi.js's createAxiosInstance: reads the
// bearer token from localStorage on every request rather than baking it in
// once, so a token refresh elsewhere in the app is picked up automatically.
const createAxiosInstance = (baseURL) => {
  const instance = axios.create({
    baseURL,
    headers: {
      "Content-Type": "application/json",
    },
  });

  instance.interceptors.request.use((config) => {
    const token = localStorage.getItem("accessToken");
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  });

  return instance;
};

const analyticsChatApi = createAxiosInstance(ANALYTICS_CHAT_URL);

// POST a new turn. Expects a 202 with the app's standard
// ApiResponse<T> envelope: { success, data: { turnId, conversationId }, error }.
export const postAnalyticsChatTurn = (conversationId, message) =>
  analyticsChatApi.post("/turns", { conversationId, message });

/**
 * Opens the SSE stream for a turn and dispatches each event to the matching
 * handler. Deliberately implemented with fetch() + a manual reader instead
 * of the native EventSource API, because EventSource cannot send an
 * Authorization header and this backend is behind the same bearer-token
 * auth as the rest of the app (see the interceptor above / commonApi.js).
 *
 * @param {string} turnId
 * @param {{onChat?: (delta: string) => void, onA2ui?: (message: object) => void,
 *          onRefusal?: (message: string) => void, onDone?: () => void,
 *          onError?: (error: Error) => void}} handlers
 * @param {AbortSignal} [signal] optional external abort signal (e.g. on unmount)
 */
export const streamAnalyticsChatTurn = async (turnId, handlers, signal) => {
  const { onChat, onA2ui, onRefusal, onDone, onError } = handlers || {};
  const token = localStorage.getItem("accessToken");

  try {
    const response = await fetch(`${ANALYTICS_CHAT_URL}/turns/${turnId}/stream`, {
      method: "GET",
      headers: {
        Accept: "text/event-stream",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      signal,
    });

    if (!response.ok || !response.body) {
      throw new Error(`Analytics chat stream failed with status ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder("utf-8");
    let buffer = "";

    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      // Normalize CRLF so the blank-line-separated-block search below only
      // has to look for a single pattern.
      buffer = buffer.replace(/\r\n/g, "\n");

      let boundary = buffer.indexOf("\n\n");
      while (boundary !== -1) {
        const rawBlock = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);
        dispatchSseBlock(rawBlock, { onChat, onA2ui, onRefusal, onDone });
        boundary = buffer.indexOf("\n\n");
      }
    }

    // Flush a trailing block that wasn't terminated by a final blank line.
    if (buffer.trim()) {
      dispatchSseBlock(buffer, { onChat, onA2ui, onRefusal, onDone });
    }
  } catch (error) {
    if (error?.name !== "AbortError") {
      onError?.(error);
    }
  }
};

// Parses one SSE block (lines starting with "event:"/"data:", already split
// on the blank line that terminates it) and dispatches it to the matching
// handler. `data:` lines are joined with "\n" per the SSE spec in case a
// payload is split across multiple data lines.
function dispatchSseBlock(block, { onChat, onA2ui, onRefusal, onDone }) {
  let eventName = "message";
  const dataLines = [];

  for (const line of block.split("\n")) {
    if (line.startsWith("event:")) {
      eventName = line.slice("event:".length).trim();
    } else if (line.startsWith("data:")) {
      dataLines.push(line.slice("data:".length).trim());
    }
  }

  const rawData = dataLines.join("\n");

  switch (eventName) {
    case "chat": {
      const payload = safeParseJson(rawData);
      if (payload && typeof payload.delta === "string") {
        onChat?.(payload.delta);
      }
      break;
    }
    case "a2ui": {
      const payload = safeParseJson(rawData);
      if (payload) {
        onA2ui?.(payload);
      }
      break;
    }
    case "refusal": {
      const payload = safeParseJson(rawData);
      onRefusal?.(payload?.message || "This question could not be answered.");
      break;
    }
    case "done": {
      onDone?.();
      break;
    }
    default:
      break;
  }
}

function safeParseJson(raw) {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch (e) {
    console.error("Analytics chat: failed to parse SSE payload", e, raw);
    return null;
  }
}
